import {
  deleteDoc,
  DocumentReference,
  Firestore,
  isObject,
  isTimestamp,
  uid,
  where,
  type CollectionReference,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { first, isString } from 'lodash';
import { type ITemporaryOrgToken } from './temporary-org-token';
import { type ITokenStorage } from './temporary-tokens';

export interface ITemporaryOrgDataToken<T extends object = object>
  extends ITemporaryOrgToken {
  data: T;
}

export function isTemporaryOrgDataToken<T extends object>(
  data: unknown
): data is ITemporaryOrgDataToken<T> {
  return (
    isObject(data) &&
    isString(data.tokenUid) &&
    isTimestamp(data.expiresAt) &&
    isString(data.orgUid) &&
    'data' in data
  );
}

/**
 * The UID is the document ID, so a repeat mint overwrites a live token. Against
 * the ~3M tokens our largest organisation already holds, one mint in ~19k
 * collides at six characters. Ten characters cost four more SMS characters and
 * make it one in ~280bn.
 */
const portalTokenUidLength = 10;

export class TemporaryOrgDataTokenStorage<T extends object>
  implements ITokenStorage<ITemporaryOrgDataToken<T>>
{
  async generate(
    data: Omit<ITemporaryOrgDataToken<T>, 'tokenUid'>,
    tokensCollection: CollectionReference<ITemporaryOrgDataToken<T>>,
    short = false
  ): Promise<{
    ref: DocumentReference<ITemporaryOrgDataToken<T>>;
    tokenUid: string;
  }> {
    const tokenUid = uid(!short, portalTokenUidLength);
    const ref = await Firestore.addDoc(
      tokensCollection,
      {
        ...data,
        tokenUid,
      },
      tokenUid
    );
    return { ref, tokenUid };
  }

  async get(
    tokenUid: string,
    tokensCollection: Query<ITemporaryOrgDataToken<T>>
  ): Promise<ITemporaryOrgDataToken<T> | undefined> {
    const data = await this._findSnapshot(tokensCollection, tokenUid);
    if (!data || !isTemporaryOrgDataToken<T>(data)) {
      return;
    }
    return data;
  }

  async has(
    tokenUid: string,
    tokensCollection: CollectionReference<ITemporaryOrgDataToken<T>>
  ): Promise<boolean> {
    const snapshot = await this._findSnapshot(tokensCollection, tokenUid);
    return snapshot ? true : false;
  }

  async remove(
    tokenUid: string,
    tokensCollection: CollectionReference<ITemporaryOrgDataToken<T>>
  ): Promise<void> {
    const snapshot = await this._findSnapshot(tokensCollection, tokenUid);
    if (!snapshot) {
      return;
    }
    await deleteDoc(snapshot.ref);
  }

  private async _findSnapshot(
    tokensCollection: Query<ITemporaryOrgDataToken<T>>,
    tokenUid: string
  ): Promise<WithRef<ITemporaryOrgDataToken<T>> | undefined> {
    const foundTokens = await Firestore.getDocs(
      tokensCollection,
      where('tokenUid', '==', tokenUid)
    );
    if (foundTokens.length > 1) {
      // eslint-disable-next-line no-console
      console.error('Refusing to resolve an ambiguous token', {
        tokenUid,
        orgUids: foundTokens.map((token) => token.orgUid),
      });
      return;
    }
    return first(foundTokens);
  }
}
